import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkle, ClockCounterClockwise, Trash, ShieldCheck } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../../lib/api";
import { formatINR } from "../../../lib/loanMath";
import { AiConsentDialog, FullDisclaimerDialog } from "./AiConsent";
import { AiQuestionnaire, DEFAULT_ANSWERS } from "./AiQuestionnaire";
import { AiResult } from "./AiResult";

function History({ rows, onOpen, onDelete, onDeleteAll }) {
  if (!rows.length) return null;
  return (
    <section className="card-flat p-4" data-testid="ai-history">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow text-[9px] flex items-center gap-1"><ClockCounterClockwise size={11} /> Your AI history ({rows.length})</div>
        <button type="button" onClick={onDeleteAll} className="text-[10px] underline text-muted-foreground hover:text-destructive" data-testid="ai-history-delete-all">Delete all</button>
      </div>
      <div className="space-y-1 text-xs">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-2 border hairline p-2" data-testid={`ai-history-row-${r.id}`}>
            <button type="button" onClick={() => onOpen(r.id)} className="text-left flex-1 min-w-0 hover:text-[hsl(var(--secondary))]" data-testid={`ai-history-open-${r.id}`}>
              <span className="text-muted-foreground">{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · </span>
              <span className="truncate">{r.headline}</span>
              {r.net_saving != null && <span className="text-muted-foreground"> · {formatINR(r.net_saving, true)}</span>}
            </button>
            <button type="button" aria-label="Delete" onClick={() => onDelete(r.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`ai-history-del-${r.id}`}><Trash size={11} /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

function WithdrawBox({ onWithdraw }) {
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(false);
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="underline" data-testid="ai-withdraw-open">Withdraw consent</button>;
  return (
    <div className="border hairline rounded p-3 space-y-2 w-full" data-testid="ai-withdraw-box">
      <div>Withdrawing consent turns off AI Suggestions for your account.</div>
      <label className="flex items-center gap-2"><input type="checkbox" checked={del} onChange={(e) => setDel(e.target.checked)} data-testid="ai-withdraw-delete-history" /> Also delete my saved AI history</label>
      <div className="flex gap-2">
        <button type="button" onClick={() => onWithdraw(del)} className="btn-primary px-3 py-1 text-xs" data-testid="ai-withdraw-confirm">Withdraw consent</button>
        <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-3 py-1 text-xs border hairline rounded">Cancel</button>
      </div>
    </div>
  );
}

/** Feature 7 — AI Loan Repayment Suggestions tab. AI picks actions & words; Estima's calculator makes every number. */
export default function AiSuggestionsPanel({ loans, planName, onOpenSimulator }) {
  const [cfg, setCfg] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState(DEFAULT_ANSWERS);
  const [modelId, setModelId] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const touched = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ai-suggestions/config");
      setCfg(data);
      setModelId((m) => m || data.default_model);
      if (!data.consent.consented) setConsentOpen(true);
      else {
        const h = await api.get("/ai-suggestions/history");
        setHistory(h.data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Please sign in to use AI Suggestions");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const firstId = history[0]?.id;
  useEffect(() => {
    if (!firstId || touched.current) return;
    api.get(`/ai-suggestions/${firstId}`).then(({ data }) => {
      if (data?.inputs?.answers && !touched.current) setAnswers({ ...DEFAULT_ANSWERS, ...data.inputs.answers });
      touched.current = true;
    }).catch(() => { /* pre-fill is optional */ });
  }, [firstId]);

  const agree = async ({ read, data }) => {
    setBusy(true);
    try {
      await api.post("/ai-suggestions/consent", { disclaimer_version: cfg.disclaimer_version, read_ack: read, data_ack: data });
      setConsentOpen(false);
      toast.success("Thanks — AI Suggestions are now on");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save consent"); } finally { setBusy(false); }
  };
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai-suggestions/generate", { loans, answers, model_id: modelId, plan_name: planName }, { timeout: 60000 });
      setResult(data);
      setCfg((c) => ({ ...c, remaining_today: data.remaining_today ?? c.remaining_today }));
      const h = await api.get("/ai-suggestions/history");
      setHistory(h.data);
    } catch (e) {
      if (e?.response?.data?.detail === "consent_required") setConsentOpen(true);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not get a suggestion");
    } finally { setBusy(false); }
  };
  const open = async (id) => { try { const { data } = await api.get(`/ai-suggestions/${id}`); setResult(data); } catch { toast.error("Could not open"); } };
  const del = async (id) => { try { await api.delete(`/ai-suggestions/${id}`); setHistory((h) => h.filter((r) => r.id !== id)); if (result?.id === id) setResult(null); } catch { toast.error("Could not delete"); } };
  const delAll = async () => { try { await api.delete("/ai-suggestions"); setHistory([]); setResult(null); toast.success("AI history deleted"); } catch { toast.error("Could not delete"); } };
  const withdraw = async (deleteHistory) => {
    try {
      await api.post("/ai-suggestions/consent/withdraw", { delete_history: deleteHistory });
      setResult(null); setHistory([]); toast.success("Consent withdrawn — AI Suggestions are off");
      await load();
    } catch { toast.error("Could not withdraw consent"); }
  };

  if (!cfg) return <div className="text-xs text-muted-foreground" data-testid="ai-loading">Loading AI Suggestions…</div>;
  const consented = cfg.consent.consented;
  return (
    <div className="space-y-4" data-testid="ai-suggestions-panel">
      <div className="card-flat p-4 flex flex-wrap items-start gap-3">
        <Sparkle size={22} weight="duotone" className="text-[hsl(var(--primary))] shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <div className="font-serif text-xl">AI repayment suggestions</div>
          <p className="text-xs text-muted-foreground mt-1">Tell us your goal. The AI suggests a plan in plain English, and Estima's own calculator works out every number you see.</p>
        </div>
      </div>
      {!consented ? (
        <div className="card-flat p-6 text-center space-y-3" data-testid="ai-locked">
          <ShieldCheck size={28} weight="duotone" className="mx-auto text-muted-foreground" />
          <p className="text-sm">AI Suggestions need your consent before first use.</p>
          <button type="button" onClick={() => setConsentOpen(true)} className="btn-primary px-4 py-2 text-sm" data-testid="ai-open-consent">Review and give consent</button>
        </div>
      ) : !loans.length ? (
        <div className="card-flat p-6 text-sm text-center" data-testid="ai-no-loans">Add your loans in My Loans first.</div>
      ) : (
        <>
          {!cfg.enabled && <div className="text-xs text-[hsl(var(--secondary))]" data-testid="ai-disabled-note">AI suggestions are unavailable right now - you'll see Estima's standard recommendation.</div>}
          <AiQuestionnaire a={answers} set={(p) => { touched.current = true; setAnswers((x) => ({ ...x, ...p })); }} models={cfg.models} modelId={modelId} setModelId={setModelId}
                           remaining={cfg.remaining_today} limit={cfg.daily_limit} onSubmit={generate} busy={busy} />
          {result && <AiResult s={result} onUpdate={(p) => setResult((r) => ({ ...r, ...p }))} onReadDisclaimer={() => setFullOpen(true)}
                               onOpenSimulator={(a) => onOpenSimulator?.(a ? { loan_name: a.loan_name, amount: a.amount } : null)} />}
          <History rows={history} onOpen={open} onDelete={del} onDeleteAll={delAll} />
        </>
      )}
      <div className="text-[11px] text-muted-foreground flex flex-wrap gap-3 items-start">
        <button type="button" onClick={() => setFullOpen(true)} className="underline" data-testid="ai-panel-full-disclaimer">Read full disclaimer</button>
        {consented && <WithdrawBox onWithdraw={withdraw} />}
      </div>
      <AiConsentDialog open={consentOpen} onOpenChange={setConsentOpen} text={cfg.full_disclaimer} onAgree={agree} busy={busy} />
      <FullDisclaimerDialog open={fullOpen} onOpenChange={setFullOpen} text={cfg.full_disclaimer} />
    </div>
  );
}
